<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UIKit from '@pocketprep/ui-kit'
import { groupModule } from '@/store/group/module'
import { studentModule } from '@/store/student/module'
import { statsModule } from '@/store/stats/module'
import { examModule } from '@/store/exam/module'
import { userModule } from '@/store/user/module'
import { organizationModule } from '@/store/organization/module'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'
import FilterDropdown from '@/components/Filters/FilterDropdown.vue'
import type { ComponentPublicInstance } from 'vue'
import type { Study } from '@pocketprep/types'
import { getActivityInfo, monthNames, type TActivityMode, stripHtmlTags } from '@/utils'
import type { TQuestion, TQuestions } from '@/store/types'

const {
    QuizContainer,
    Button: PocketButton,
    KeyboardShortcutsButton,
    Icon,
    Tooltip,
    KeyboardShortcutsModal,
    Question,
    QuestionReviewPagination,
    GlobalMetricsToggle,
} = UIKit

// UIKit Question component ChoiceKey props
type TChoiceKey = `${'a' | 'd'}${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`
type TMatrixChoiceKey = `${'a' | 'd'}${1 | 2 | 3}_${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12}`
type TBuildListChoiceKey = `a${1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9}`

const route = useRoute()
const router = useRouter()

const isLoading = ref(true)
const brandColors = BrandColors
const quizContainer = ref<ComponentPublicInstance | null>(null)
const filterType = ref('answered')
const showSideBar = ref(true)
const listViewHover = ref(false)
const fullViewHover = ref(false)
const showListViewTooltip = ref(false)
const showFullViewTooltip = ref(false)
const isShowingGridTooltip = ref(false)
const filterSubjects = ref<string[]>([])
const showFilterDropdown = ref(false)
const checkboxSubjects = ref<string[]>([])
const showGroupMetrics = ref(false)
const showGroupsDropdown = ref(false)

const emit = defineEmits<{
    close: []
}>()

const filterQuestions = ref<TQuestions>({})

const studentStats = computed(() => {
    return statsModule.getters.getCurrentStudentStats()
})

const groupStats = computed(() => {
    return statsModule.getters.getCurrentGroupAggStats()
})

const studentUserInfo = computed(() => {
    return studentStats.value.userInfo
})

const currentStudentName = computed(() => {
    const currentStudent = studentModule.getters.getCurrentStudent()

    if (currentStudent?.displayFirstName || currentStudent?.displayLastName) {
        return {
            firstName: currentStudent?.displayFirstName,
            lastName: currentStudent?.displayLastName,
        }
    } else {
        return {
            firstName: studentStats.value.userInfo.firstName || '',
            lastName: studentStats.value.userInfo.lastName || '',
        }
    }
})

const currentGroupExam = computed(() => {
    return examModule.getters.getCurrentGroupExam()
})

const currentExamMetadata = computed(() => {
    return examModule.getters.getCurrentExamMetadata()
})

const currentGroupName = computed(() => {
    return groupModule.getters.getCurrentGroup()?.name
})

const currentGroupId = computed(() => {
    return groupModule.getters.getCurrentGroup()?.objectId
})

const isQuestionFlagged = (serial: string) => {
    return !!studentFlaggedQuestions.value.find(question => question.question.serial === serial)
}

const formattedReplacedPrompt = (prompt: string) => {
    return prompt.replace(/<p>/g, '')
        .replace(/<\/p>/g, '')
        .replace(/<strong>/g, '')
        .replace(/<\/strong>/g, '')
}

const formattedQuestionPrompt = (question: TQuestion) => {
    const formattedPrompt = formattedReplacedPrompt(question.question.prompt)
    return question.scenarioPartLabel ? `${question.scenarioPartLabel}: ${formattedPrompt}` : formattedPrompt
}

const isCurrentQuestionFlagged = computed(() => {
    return studentFlaggedQuestions.value.find(q => q.question && q.question.serial === currentSerial.value)
})

const currentExamQuestionsBySerial = computed(() => {
    return examModule.getters.getCurrentExamQuestionsBySerial()
})

const examActivityForQuizId = computed(() => {
    return studentStats.value.allActivity.find(a => {
        return a.quizId === route.query.quizId
    })
})

// Get answered questions with filters applied
const studentAnsweredQuestions = computed(() => {
    if (filterSubjects.value.length && studentStats.value.answeredQuestions.length) {
        return studentStats.value.answeredQuestions.filter(q => {
            const s = q.question && q.question.subject as Study.Class.SubjectJSON
            return filterSubjects.value.includes(q.question && s.name)
        })
    }
    return studentStats.value.answeredQuestions
})

// Get flagged questions with filters applied
const studentFlaggedQuestions = computed(() => {
    const flaggedSerials = studentStats.value.flaggedQuestions.map(q => q.serial)

    // Array of QuestionJSON entries
    const flaggedQuestionsList: TQuestion[] = []

    flaggedSerials.forEach(serial => {
        const flaggedQuestion = studentAnsweredQuestions.value.find(q => q.question.serial === serial)

        // Flagged and answered
        if (flaggedQuestion) {
            flaggedQuestionsList.push(flaggedQuestion)
        } else {
            // It's a flagged serial but NOT answered... need it anyway
            const question = currentExamQuestionsBySerial.value[serial]
            if (question) {
                flaggedQuestionsList.push({ question })
            }
        }
    })
    if (flaggedQuestionsList.length && filterSubjects.value.length) {
        return flaggedQuestionsList.filter(q => {
            const s = q.question && q.question.subject as Study.Class.SubjectJSON
            return filterSubjects.value.includes(q.question && s.name)
        })
    }
    return flaggedQuestionsList
})

// Get correct questions with filters applied
const studentCorrectQuestions = computed(() => {
    const correctQuestions = studentAnsweredQuestions.value.filter(q => q.answer.isCorrect)

    if (filterSubjects.value.length && correctQuestions.length) {
        return correctQuestions.filter(q => {
            const s = q.question.subject as Study.Class.SubjectJSON
            return filterSubjects.value.includes(q.question && s.name)
        })
    }

    return correctQuestions
})

// Get incorrect questions with filters applied
const studentIncorrectQuestions = computed(() => {
    const incorrectQuestions = studentAnsweredQuestions.value.filter(q => !q.answer.isCorrect)
    if (filterSubjects.value.length && incorrectQuestions.length) {
        return incorrectQuestions.filter(q => {
            const s = q.question.subject as Study.Class.SubjectJSON
            return filterSubjects.value.includes(q.question && s.name)
        })
    }
    return incorrectQuestions
})

// If an exam activity is passed in, grab the questions for it
const examActivityQuestions = computed(() => {
    const activityAnswers = examActivityForQuizId.value?.answers

    const questionsForActivity: TQuestion[] = []

    if (activityAnswers) {
        activityAnswers.forEach(quizAnswer => {
            const q = currentExamQuestionsBySerial.value[quizAnswer.questionSerial]
            if (q) {
                questionsForActivity.push({
                    question: q,
                    answer: quizAnswer,
                })
            }
        })
    }
    return questionsForActivity
})

const globalMetricsForQuestion = computed((): Study.Class.GlobalQuestionMetricJSON | null => {
    if (!showGroupMetrics.value || !currentQuestion.value) {
        return null
    }
    return currentGroupMetrics(currentQuestion.value.serial) as unknown as Study.Class.GlobalQuestionMetricJSON
})

const getActivityName = (mode: TActivityMode) => {
    return getActivityInfo(mode).name
}

const questionScenarioPartLabel = (question: Study.Class.QuestionJSON) => {
    return examModule.getters.getScenarioPartLabel(question)
}

const questionType = computed(() => {
    return (filterType.value || (route.query.type as string) || 'answered')
})

const questions = computed(() => {
    // Function that builds the list of questions for the current QUESTION_REVIEW context
    if (route.query.quizId) {
        const qList = examActivityQuestions.value.reduce<TQuestions>((acc, question) => {
            const isAnswered = question.answer
            const isFlagged = isQuestionFlagged(question.question.serial)
            const isCorrect = question.answer?.isCorrect
            const subject = question.question.subject as Study.Class.SubjectJSON
            const promptStripped = stripHtmlTags(question.question.prompt)
            question.question.prompt = promptStripped
            const scenarioLabel = questionScenarioPartLabel(question.question)
            if (scenarioLabel) {
                question.scenarioPartLabel = scenarioLabel
            }

            const checkFilterSubjects = filterSubjects.value.length === 0
                || filterSubjects.value.length > 0 && filterSubjects.value.includes(subject.name)

            if (isAnswered && checkFilterSubjects) {
                acc.answered?.push(question)
            }
            if (isFlagged && checkFilterSubjects) {
                acc.flagged?.push(question)
            }
            if (!isCorrect && isAnswered && checkFilterSubjects) {
                acc.incorrect?.push(question)
            }
            if (isCorrect && checkFilterSubjects) {
                acc.correct?.push(question)
            }
            return acc
        }, { answered: [], flagged: [], incorrect: [], correct: [] })
        return qList
    }
    return {
        answered: studentAnsweredQuestions.value.filter(q => q.question && !q.question.isMockQuestion),
        flagged: studentFlaggedQuestions.value.filter(q => q.question && !q.question.isMockQuestion),
        correct: studentCorrectQuestions.value.filter(q => q.question && !q.question.isMockQuestion),
        incorrect: studentIncorrectQuestions.value.filter(q => q.question && !q.question.isMockQuestion),
    } as TQuestions
})

const pathName = computed(() => {
    return route.name as string
})

const questionTypeLabel = computed(() => {
    return questionType.value === 'flagged'
        ? 'Flagged Questions'
        : questionType.value === 'answered'
            ? 'Answered Questions'
            : questionType.value === 'correct'
                ? 'Correct Questions'
                : questionType.value === 'incorrect' ? 'Incorrect Questions' : 'answered'
})

const questionCount = computed(() => {
    return questions.value[questionType.value]?.length || 0
})

const previousPage = computed(() => {
    if (route.path.search('/stats/') > -1) {
        return 'Stats'
    }

    if (route.path.search('/content/') > -1) {
        return 'Questions'
    }

    return ''
})

const currentSerial = computed(() => {
    return (route.query.questionSerial as string) || questions.value[questionType.value]?.[0]?.question?.serial
})

const questionIndex = computed((): number => {
    return questions.value[questionType.value]?.length
        ? (questions.value[questionType.value]?.findIndex(q => q.question.serial === currentSerial.value) ?? -1)
        : -1
})

const nextQuestion = computed(() => {
    return questions.value[questionType.value]?.[questionIndex.value + 1]
})

const previousQuestion = computed(() => {
    return questions.value[questionType.value]?.[questionIndex.value - 1]
})

const changePage = (newPage: number) => {
    if (newPage > questionIndex.value) {
        clickNext()
    } else if (newPage < questionIndex.value) {
        clickPrevious()
    }
}

const isPreviousArrowDisabled = computed(() => {
    return questionIndex.value === 0 || questionIndex.value === -1
})

const isNextArrowDisabled = computed(() => {
    return questionIndex.value >= (questions.value[questionType.value]?.length ?? 1) - 1
})

const quizContainerEl = computed(() => {
    const quizContainerRef = quizContainer.value
    return (quizContainerRef && '$el' in quizContainerRef) ? quizContainerRef.$el : null
})

const s3Url = computed(() => {
    return import.meta.env.VUE_APP_S3_URL
})

const formattedDate = computed(() => {
    if (examActivityForQuizId.value) {
        const date = new Date(examActivityForQuizId.value.date)
        return `${monthNames[date.getMonth()]?.substring(0, 3)} ${date.getDate()}, ${date.getFullYear()}`
    }
    return null
})

const currentSubject = computed(() => {
    const subject = currentQuestion.value?.subject as Study.Class.SubjectJSON
    return subject.name
})

const currentQuestion = computed(() => {
    const list = questions.value?.[questionType.value]
    if (list && questionIndex.value > -1 && list.length > 0) {
        const currQuestion = list[questionIndex.value]
        if (currQuestion) {
            return currQuestion.question
        }
    }
    return null
})

const selectedChoices = computed(() => {
    const q = questions.value[questionType.value]?.[questionIndex.value]
    return (q?.answer?.selectedChoices || []) as TChoiceKey[]
})

const selectedChoicesMatrixTyped = computed(() => {
    const q = questions.value[questionType.value]?.[questionIndex.value]
    return (q?.answer?.selectedChoices || []) as TMatrixChoiceKey[]
})

const selectedChoicesBuildListTyped = computed(() => {
    const q = questions.value[questionType.value]?.[questionIndex.value]
    return (q?.answer?.selectedChoices || []) as TBuildListChoiceKey[]
})

const quizSettings = computed(() => {
    return userModule.state.user?.quizSettings || null
})

const allowKeyboardShortcuts = computed(() => {
    return !!quizSettings.value?.enableKeyboardShortcuts
})

const currentVisibleOrgGroups = computed(() => {
    return groupModule.getters.getCurrentVisibleOrgGroups()
})

const studentOrgGroups = computed(() => {
    const studentCurrentOrgGroupIds = groupModule.getters.searchCurrentOrgGroups({
        studentId: route.params.studentId as string,
        examId: currentGroupExam.value?.objectId,
    }).map(o => o.objectId)

    return currentVisibleOrgGroups.value.filter(visibleOrgGroup => {
        return studentCurrentOrgGroupIds.includes(visibleOrgGroup.objectId)
    })
})

const examMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const groupFlagged = computed(() => {
    return groupStats.value.questionStats.filter(q => !q.isMockQuestion && q.numStudentsFlagged > 0)
})

const currentGroupMetrics = (serial: string) => {
    const question = groupStats.value.questionStats.find(q => q.serial === serial)
    const numStudentsAnswered = question?.answers.length || 0

    const numStudentsCorrect = question?.answers.filter(a => a.isCorrect).length || 0

    const numStudentsFlagged = groupFlagged.value.find(fq => fq.serial === serial)?.numStudentsFlagged
        || 0

    const selectedChoicesByCount = question?.answers.reduce((acc, answer) => {
        answer.selectedChoices.forEach(choice => {
            acc[choice] = (acc[choice] ?? 0) + 1
        })
        return acc
    }, {} as { [key: string]: number }) || {
        'a1': 0,
        'b1': 0,
        'c1': 0,
        'd1': 0,
        'a2': 0,
        'b2': 0,
        'c2': 0,
        'd2': 0,
        'a3': 0,
        'b3': 0,
        'c3': 0,
        'd3': 0,
        'a4': 0,
        'b4': 0,
        'c4': 0,
        'd4': 0,
    }

    const cge = currentGroupExam.value?.examMetadata.objectId as string
    const metadata = examMetadata.value[cge]
    const examGuid = metadata?.examGuid ?? ''
    const appId = metadata?.appId ?? ''

    return {
        answeredCorrectlyCount: numStudentsCorrect,
        answeredIncorrectlyCount: numStudentsAnswered - numStudentsCorrect,
        questionSerial: serial,
        examGuid,
        examType: appId,
        choiceStats: selectedChoicesByCount,
        numStudentsAnswered,
        percentStudentsAnsweredCorrectly: numStudentsAnswered
            ? Math.round((numStudentsCorrect / numStudentsAnswered) * 100) || 0
            : 0,
        numStudentsFlagged,
    }
}

const goBack = () => {
    emit('close')
}

const clickPrevious = () => {
    const prev = previousQuestion.value
    if (prev) {
        router.replace({
            name: pathName.value,
            query: {
                ...route.query,
                questionSerial: prev.question.serial,
                quizId: route.query.quizId,
            },
        })
    }
}

const clickNext = () => {
    const next = nextQuestion.value
    if (next) {
        router.replace({
            name: pathName.value,
            query: {
                ...route.query,
                questionSerial: next.question.serial,
                quizId: route.query.quizId,
            },
        })
    }
}

const toggleGlobalMetrics = (newVal: boolean) => {
    showGroupMetrics.value = newVal
}

const listQuestionClick = (serial: string) => {
    if (serial !== currentSerial.value) {
        if (filterSubjects.value.length) {
            router.replace({
                name: pathName.value,
                query: {
                    ...route.query,
                    questionSerial: serial,
                    type: questionType.value,
                    subject: filterSubjects.value,
                },
            })
            return
        }

        router.replace({
            name: pathName.value,
            query: {
                ...route.query,
                questionSerial: serial,
                type: questionType.value,
            },
        })
    }
}

const toggleKeyboardShortcuts = (newVal: boolean) => {
    userModule.actions.updateQuizSettings({
        enableKeyboardShortcuts: newVal,
    })
}

/* istanbul ignore next */
const listViewMouseEnter = () => {
    listViewHover.value = true
    showListViewTooltip.value = true
}

/* istanbul ignore next */
const listViewMouseLeave = () => {
    listViewHover.value = false
    showListViewTooltip.value = false
}

/* istanbul ignore next */
const fullViewMouseEnter = () => {
    fullViewHover.value = true
    showFullViewTooltip.value = true
}

/* istanbul ignore next */
const fullViewMouseLeave = () => {
    fullViewHover.value = false
    showFullViewTooltip.value = false
}

const updateFilter = ({ questionType: qt, subjects, trimmedSubjects }:
{ questionType: string; subjects: string[]; trimmedSubjects: string[] }) => {
    filterType.value = qt
    filterSubjects.value = trimmedSubjects
    checkboxSubjects.value = subjects
    const isCurrentQuestionWithinFilter = questions.value[qt]?.some(q => q.question.serial === currentSerial.value)

    router.replace({
        name: pathName.value,
        query: {
            ...route.query,
            questionSerial: questions.value[qt]?.length && !isCurrentQuestionWithinFilter
                ? questions.value[qt]?.[0]?.question.serial
                : currentSerial.value,
            type: qt,
            subject: trimmedSubjects,
        },
    })
}

const updateGroup = (groupId: string) => {
    if (route.params.groupId !== groupId) {
        router.replace({
            name: route.name as string,
            params: {
                groupId,
                studentId: route.params.studentId,
            },
            query: {
                ...route.query,
            },
        })
    }
}

onMounted(async () => {
    const allExamMetadata = await examModule.actions.fetchExamMetadata()
    const currentExam = await examModule.actions.fetchCurrentGroupExam()
    const currentExamMetadataVal = currentExam?.examMetadata.objectId
        && allExamMetadata[currentExam.examMetadata.objectId]
    const currentOrganization = await organizationModule.actions.fetchCurrentOrganization()

    if (currentExamMetadataVal && currentOrganization) {
        await examModule.actions.fetchQuestionsByExam({
            compositeKey: currentExamMetadataVal.compositeKey,
            organizationId: currentOrganization.objectId,
            examMetadataId: currentExamMetadataVal.objectId,
        })
    }

    const routeGroupId = route.params.groupId
    const routeStudentId = route.params.studentId
    await Promise.all([
        groupModule.actions.updateCurrentGroupId(routeGroupId as string),
        studentModule.actions.updateCurrentStudentId(routeStudentId as string),
        studentModule.actions.fetchCurrentStudent(),
        groupModule.actions.fetchCurrentGroup(),
        statsModule.actions.fetchCurrentStudentStats(),
    ])

    if (route.query.subjects as string[]) {
        filterSubjects.value = route.query.subjects as string[]
    }

    filterQuestions.value = questions.value

    isLoading.value = false
})

watch([ () => route.params.groupId, () => route.params.studentId ], ([ newGroupId, newStudentId ]) => {
    if (newGroupId && newStudentId) {
        groupModule.actions.updateCurrentGroupId(newGroupId as string)
        studentModule.actions.updateCurrentStudentId(newStudentId as string)
        studentModule.actions.fetchCurrentStudent()
        groupModule.actions.fetchCurrentGroup()
        examModule.actions.fetchCurrentGroupExam()
        statsModule.actions.fetchCurrentStudentStats()
    }
})

watch(() => route.query.type, (type) => {
    if (type) {
        filterType.value = type as string
    }
}, { immediate: true })
</script>

<template>
    <div class="student-question-review">
        <QuizContainer
            v-if="studentStats && studentAnsweredQuestions"
            ref="quizContainer"
            class="student-question-review"
            :show-side="showSideBar"
        >
            <template #header>
                <PocketButton
                    class="student-question-review__back-btn"
                    type="icon-yellow"
                    @click="goBack"
                >
                    <Icon class="student-question-review__back-btn-icon" type="arrow" />
                    Back to {{ previousPage }}
                </PocketButton>
                <div
                    v-if="route.query.quizId && examActivityForQuizId"
                >
                    <div
                        class="student-question-review__frame-context-quiz"
                    >
                        <div
                            class="student-question-review__student-name"
                        >
                            {{ currentStudentName.firstName }}
                            {{ currentStudentName.lastName }}
                        </div>
                        <div class="student-question-review__bullet">
                            •
                        </div>
                        <div
                            class="student-question-review__activity-name"
                        >
                            {{ getActivityName(examActivityForQuizId.mode) }}
                        </div>
                        <div class="student-question-review__bullet">
                            •
                        </div>
                        <div
                            class="exam-activity__date"
                        >
                            {{ formattedDate }}
                        </div>
                    </div>
                </div>
                <div
                    v-else
                    class="student-question-review__frame-context"
                >
                    {{ currentStudentName.firstName }}
                    {{ currentStudentName.lastName }}'s
                    {{ questionTypeLabel }}
                </div>
                <QuestionReviewPagination
                    class="student-question-review__pagination"
                    :total="questionCount"
                    :current-page="questionIndex"
                    @changePage="changePage($event)"
                />
            </template>
            <template #side>
                <div v-if="showSideBar" class="student-question-review__side-bar">
                    <div class="student-question-review__side-bar-header">
                        Questions
                        <PocketButton
                            type="icon-yellow"
                            :icon="(filterSubjects.length) ? 'filterActive' : 'filter'"
                            :aria-label="`${
                                (filterSubjects.length) ? 'Filters selected, ' : ''
                            }Show filters`"
                            @click="showFilterDropdown = !showFilterDropdown"
                            @keypress.enter="showFilterDropdown = !showFilterDropdown"
                            @mousedown.prevent
                        />
                        <FilterDropdown
                            v-if="showFilterDropdown"
                            class="student-question-review__filter-dropdown"
                            :default-question-type="filterType"
                            :default-subjects="filterSubjects"
                            :questions="filterQuestions[questionType]"
                            :student-or-group="'student'"
                            aria-live="polite"
                            @filterChange="updateFilter"
                            @close="showFilterDropdown = false"
                        />
                    </div>
                    <div
                        v-if="questions[questionType]?.length"
                        class="student-question-review__list"
                    >
                        <div
                            v-for="(q, index) in questions[questionType]"
                            :key="index"
                            tabindex="0"
                            class="student-question-review__list-prompt"
                            :class="{
                                'student-question-review__list-prompt--active': index === questionIndex,
                            }"
                            @click="listQuestionClick(q.question.serial)"
                            @keydown.enter.stop.prevent="listQuestionClick(q.question.serial)"
                            @mousedown.prevent
                            v-html="formattedQuestionPrompt(q)"
                        />
                    </div>
                    <div
                        v-if="
                            (questionType === 'flagged' && !questions[questionType]?.length) ||
                                (questionType === 'answered' && !questions[questionType]?.length) ||
                                (questionType === 'correct' && !questions[questionType]?.length) ||
                                (questionType === 'incorrect' && !questions[questionType]?.length)
                        "
                        class="student-question-review__list-empty"
                    >
                        Looks like there aren’t any questions to match your filter criteria.
                    </div>
                </div>
            </template>
            <template #question>
                <Icon
                    v-if="isLoading"
                    class="group-question-review__loading"
                    type="loading2"
                />
                <Question
                    v-if="!isLoading && currentQuestion"
                    :key="`${currentQuestion.objectId}-${allowKeyboardShortcuts}`"
                    class="student-question-review__question"
                    :container-el="quizContainerEl"
                    :question="currentQuestion"
                    :question-number="questionIndex + 1"
                    :quiz-length="questionCount"
                    :previous-choices="selectedChoices"
                    :previous-matrix-choices="selectedChoicesMatrixTyped"
                    :previous-build-list-choices="selectedChoicesBuildListTyped"
                    :previous-multi-part-multiple-choice-choices="selectedChoices"
                    :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                    :image-url-prefix="`${s3Url || ''}/${currentQuestion.compositeKey.toUpperCase()}/`"
                    :review-mode="true"
                    :default-show-explanation=" true"
                    :auto-focus-prompt="true"
                    :hide-references="currentExamMetadata?.hideReferences"
                    :global-metrics="globalMetricsForQuestion"
                    @close="goBack"
                    @previousQuestion="clickPrevious"
                    @nextQuestion="clickNext"
                >
                    <template #context>
                        <div class="student-question-review__subject">
                            {{ currentSubject }}
                        </div>
                    </template>
                    <template #metricsTiles>
                        <div
                            v-if="showGroupMetrics"
                            class="student-question-review__stats-summary"
                        >
                            <div class="student-question-review__stats-summary-total">
                                <div class="student-question-review__stats-summary-value">
                                    {{ currentGroupMetrics(currentQuestion.serial).numStudentsAnswered }}
                                </div>
                                <div class="student-question-review__stats-summary-label">
                                    Students Answered
                                </div>
                            </div>
                            <div class="student-question-review__stats-summary-correct">
                                <div class="student-question-review__stats-summary-value">
                                    {{ currentGroupMetrics(currentQuestion.serial).percentStudentsAnsweredCorrectly }}%
                                </div>
                                <div class="student-question-review__stats-summary-label">
                                    Answered Correctly
                                </div>
                            </div>
                            <div class="student-question-review__stats-summary-flagged">
                                <div class="student-question-review__stats-summary-value">
                                    {{ currentGroupMetrics(currentQuestion.serial).numStudentsFlagged }}
                                </div>
                                <div class="student-question-review__stats-summary-label">
                                    Students Flagged
                                </div>
                            </div>
                        </div>
                    </template>
                </Question>
                <div v-else class="student-question-review__no-question">
                    <div class="student-question-review__no-question-title">
                        No Results.
                    </div>
                    <div class="student-question-review__no-question-subtext">
                        No question is selected.
                    </div>
                </div>
            </template>
            <template #footer>
                <div class="student-question-review__view-toggles">
                    <div
                        class="student-question-review__side-toggle"
                        :class="{
                            'student-question-review__side-toggle--with-divider': examActivityForQuizId?.mode !== 11
                        }"
                    >
                        <div
                            class="student-question-review__view-side"
                            :class="{
                                'student-question-review__view-side--active': showSideBar,
                                'student-question-review__view-side--hover': listViewHover,
                            }"
                            tabindex="0"
                            role="radio"
                            aria-label="Show List View"
                            :aria-checked="showSideBar"
                            @mouseenter="listViewMouseEnter"
                            @mouseleave="listViewMouseLeave"
                            @mousedown.prevent
                            @focus="showListViewTooltip = true"
                            @blur="showListViewTooltip = false"
                            @click="showSideBar = true"
                            @keydown.enter.stop.prevent="showSideBar = true"
                        >
                            <Tooltip
                                v-if="showListViewTooltip"
                                :styles="{
                                    'backgroundColor': brandColors.grayDivider || '',
                                    'color': brandColors.brandBlack || '',
                                }"
                            >
                                List View
                            </Tooltip>
                            <Icon
                                v-if="showSideBar || listViewHover"
                                class="student-question-review__view-side-active-icon"
                                type="sideBarActive"
                            />
                            <Icon
                                v-else
                                class="student-question-review__view-side-icon"
                                type="sideBar"
                            />
                        </div>
                        <div
                            class="student-question-review__view-full"
                            :class="{
                                'student-question-review__view-full--active': !showSideBar,
                                'student-question-review__view-full--hover': fullViewHover,
                            }"
                            tabindex="0"
                            role="radio"
                            aria-label="Show Full View"
                            :aria-checked="!showSideBar"
                            @mouseenter="fullViewMouseEnter"
                            @mouseleave="fullViewMouseLeave"
                            @mousedown.prevent
                            @focus="showFullViewTooltip = true"
                            @blur="showFullViewTooltip = false"
                            @click="showSideBar = false"
                            @keydown.enter.stop.prevent="showSideBar = false"
                        >
                            <Tooltip
                                v-if="showFullViewTooltip"
                                :styles="{
                                    'backgroundColor': brandColors.grayDivider || '',
                                    'color': brandColors.brandBlack || '',
                                }"
                            >
                                Full View
                            </Tooltip>
                            <Icon
                                v-if="!showSideBar || fullViewHover"
                                class="student-question-review__view-full-active-icon"
                                type="fullViewActive"
                            />
                            <Icon
                                v-else
                                class="student-question-review__view-full-icon"
                                type="fullView"
                            />
                        </div>
                    </div>
                    <GlobalMetricsToggle
                        v-if="examActivityForQuizId?.mode !== 11"
                        class="student-question-review__global-metrics-toggle-left"
                        :show-global-metrics="showGroupMetrics"
                        @toggleGlobalMetrics="toggleGlobalMetrics"
                    >
                        <template #metricsTooltipText>
                            Group Stats
                        </template>
                    </GlobalMetricsToggle>
                    <span
                        v-if="showGroupMetrics"
                        class="student-question-review__groups-label"
                        :class="{ 'student-question-review__groups-multi': studentOrgGroups.length > 1 }"
                        :tabindex="studentOrgGroups.length > 1 ? 0 : -1"
                        @click="showGroupsDropdown = !showGroupsDropdown"
                        @blur="showGroupsDropdown = false"
                    >
                        {{ currentGroupName }}
                        <Icon
                            v-if="studentOrgGroups.length > 1"
                            class="student-question-review__groups-dropdown-arrow"
                            :class="{
                                'student-question-review__groups-dropdown-arrow--up': showGroupsDropdown,
                            }"
                            type="accordionArrow"
                        />
                    </span>
                    <ul v-if="showGroupsDropdown && studentOrgGroups.length > 1">
                        <li
                            v-for="group in studentOrgGroups"
                            :key="group.objectId"
                            :class="{
                                'selected': group.objectId === currentGroupId
                            }"
                            @mousedown.prevent="updateGroup(group.objectId)"
                            @click="updateGroup(group.objectId)"
                        >
                            <div>{{ group.name }}</div>
                        </li>
                    </ul>
                </div>
                <div class="student-question-review__controls">
                    <Icon
                        class="student-question-review__previous-arrow"
                        :class="{
                            'student-question-review__previous-arrow--disabled': isPreviousArrowDisabled,
                            'student-question-review__previous-arrow--flagged': studentFlaggedQuestions.length > 0,
                        }"
                        :tabindex="isPreviousArrowDisabled ? -1 : 0"
                        type="paginationArrow"
                        aria-label="Previous question"
                        :aria-disabled="isPreviousArrowDisabled"
                        @click="clickPrevious"
                        @keydown.enter.stop.prevent="clickPrevious"
                        @mousedown.prevent
                    />
                    <div
                        class="student-question-review__container"
                    >
                        <div
                            class="student-question-review__flag"
                            @focus="isShowingGridTooltip = true"
                            @blur="isShowingGridTooltip = false"
                            @mouseenter="isShowingGridTooltip = true"
                            @mouseleave="isShowingGridTooltip = false"
                        >
                            <div
                                v-if="isCurrentQuestionFlagged"
                                class="student-question-review__flag-container"
                            >
                                <Icon
                                    type="flag-filled"
                                    class="student-question-review__flagged-by-student-icon"
                                    title="Flagged"
                                />
                                <div
                                    class="student-question-review__student-name-text"
                                >
                                    {{ studentUserInfo.firstName }}
                                </div>
                                <div
                                    class="student-question-review__flagged-text"
                                >
                                    Flagged
                                </div>
                            </div>
                            <span
                                v-if="!isCurrentQuestionFlagged"
                                class="student-question-review__not-flagged-text"
                            >
                                Not Flagged
                            </span>
                        </div>
                    </div>
                    <Icon
                        tabindex="0"
                        class="student-question-review__next-arrow"
                        :class="{
                            'student-question-review__next-arrow--disabled': isNextArrowDisabled,
                            'student-question-review__next-arrow--flagged': studentFlaggedQuestions.length > 0,
                        }"
                        type="paginationArrow"
                        aria-label="Next question"
                        :aria-disabled="isNextArrowDisabled"
                        @click="clickNext"
                        @keydown.enter.stop.prevent="clickNext"
                        @mousedown.prevent
                    />
                </div>
                <KeyboardShortcutsButton
                    class="student-question-review__keyboard-shortcuts-btn"
                >
                    <template
                        #keyboardShortcutsModal="{
                            showKeyboardShortcutsModal,
                            closeModalFunc
                        }"
                    >
                        <Portal to="modal">
                            <KeyboardShortcutsModal
                                v-if="showKeyboardShortcutsModal"
                                key="questionReviewKeyboardShortcutsModal"
                                keyboard-mode="teach"
                                :allow-keyboard-shortcuts="allowKeyboardShortcuts"
                                aria-live="polite"
                                @toggleKeyboardShortcuts="toggleKeyboardShortcuts"
                                @close="closeModalFunc"
                            />
                        </Portal>
                    </template>
                </KeyboardShortcutsButton>
            </template>
        </QuizContainer>
    </div>
</template>

<style lang="scss" scoped>
.student-question-review {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1002;
    box-sizing: border-box;

    &__back-btn-icon {
        width: 14px;
        height: 12px;
        transform: rotate(180deg);
        margin-right: 8px;
    }

    &__frame-context {
        position: absolute;
        text-transform: capitalize;
        left: 50%;
        transform: translateX(-50%);
        color: $white;
        font-size: 15px;
        letter-spacing: -0.09px;
        line-height: 18px;
    }

    &__frame-context-quiz {
        left: 50%;
        color: $white;
        font-size: 15px;
        letter-spacing: -0.09px;
        line-height: 20px;
        display: flex;
    }

    &__bullet {
        padding: 1px 8px;
        font-size: 11px;
    }

    &__subject {
        width: 100%;
        max-width: 492px;
        color: $slate-01;
        margin-top: 48px;
        margin-bottom: 8px;
        padding: 0 16px;
        box-sizing: border-box;
        font-size: 14px;
    }

    &__stats-summary {
        margin-top: 24px;
        display: flex;
        width: 100%;
        max-width: 492px;
        padding-bottom: 50px;
    }

    &__stats-summary-total {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        margin-right: 12px;
        padding: 9px 0;
        border-radius: 6px;
        border: 1px solid rgba($pewter, 0.85);
        background-color: $white;
    }

    &__stats-summary-correct {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        margin-right: 12px;
        padding: 9px 0;
        border-radius: 6px;
        border: 1px solid rgba($pewter, 0.85);
        background-color: $white;
    }

    &__stats-summary-flagged {
        display: flex;
        flex-direction: column;
        align-items: center;
        flex: 1;
        padding: 9px 0;
        border-radius: 6px;
        border: 1px solid rgba($pewter, 0.85);
        background-color: $white;
    }

    &__stats-summary-value {
        font-weight: 600;
        font-size: 26px;
        letter-spacing: 0.26px;
        line-height: 31px;
    }

    &__stats-summary-label {
        color: $ash;
        font-size: 13px;
        line-height: 16px;
        text-align: center;

        &--dark {
            color: $white;
        }
    }

    &__pagination {
        display: flex;
        align-items: center;
    }

    &__page-nums {
        margin: 0 7px;
    }

    &__side-bar {
        position: relative;
        display: flex;
        flex-direction: column;
        height: 100%;
    }

    &__filter-dropdown {
        position: absolute;
        left: 0;
        top: 44px;
        z-index: 2;
    }

    &__active-indicator {
        position: absolute;
        right: -10px;
        transform: translateY(-50%);
        width: 10px;
        height: 18px;
        background-color: $ash;
        border: 5px solid white;
    }

    &__side-bar-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: 44px;
        padding: 0 6px 0 12px;
        font-weight: 600;
        font-size: 16px;
        line-height: 19px;
    }

    &__list {
        position: relative;
        overflow-y: auto;
        flex: 1;
    }

    &__list-empty {
        color: $white;
        font-size: 13px;
        max-width: 160px;
        margin: 58px auto 0;
        line-height: 18px;
        opacity: 0.82;
        text-align: center;
    }

    &__list-prompt {
        position: relative;
        box-sizing: border-box;
        color: rgba($white, 0.75);
        padding: 14px 11px 14px 12px;
        font-size: 14px;
        letter-spacing: -0.09px;
        line-height: 17px;
        cursor: pointer;
        border-top: 2px solid $jet;
        outline: none;

        &:last-child {
            border-bottom: 2px solid $mariner;
        }

        &--active {
            color: $white;
            background-color: $ash;
            border-radius: 6px;
            border-color: $charcoal;

            &:last-child {
                border-bottom: 0;
            }
        }

        &--active + & {
            border-top-color: $charcoal;
        }

        &:hover {
            color: $white;
        }

        &:focus {
            color: $white;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                bottom: 0;
                left: 0;
                right: 0;
                border-radius: 6px;
                border: 1px solid $butterscotch;
            }
        }
    }

    &__loading {
        position: absolute;
        left: 50%;
        top: 25%;
        transform: translateX(-50%) scale(1.5);
    }

    &__question {
        position: relative;
        height: 100%;
    }

    &__no-question {
        max-width: 242px;
        margin: 158px auto 0;
        text-align: center;
    }

    &__no-question-title {
        font-size: 16px;
        line-height: 19px;
        margin-bottom: 9px;
        font-weight: 600;
    }

    &__no-question-subtext {
        font-size: 14px;
        line-height: 17px;
        max-width: 242px;
        color: $ash;
    }

    &__view-toggles {
        display: flex;
        align-items: center;
    }

    &__side-toggle {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 80px;
        height: 37px;
        margin-right: 25px;
        border-radius: 6px;

        &--with-divider {
            &::after {
                content: '';
                position: absolute;
                height: 100%;
                right: -13px;
                border-right: 1px solid $slate-03;
            }
        }
    }

    &__view-side,
    &__view-full {
        position: absolute;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 40px;
        height: 37px;
        border-radius: 6px;
        outline: none;

        &--hover {
            background-color: rgba($white, 0.1);
        }

        &--active {
            background-color: $charcoal;
            z-index: 2;
        }

        &:focus::before {
            content: '';
            position: absolute;
            border: 1px solid $butterscotch;
            width: 40px;
            height: 36px;
            top: 0;
            left: 0;
            border-radius: 4px;
            box-sizing: border-box;
        }

        &:not(&--active) {
            cursor: pointer;
        }

        .uikit-tooltip {
            position: absolute;
            left: calc(50%);
            top: calc(-100%);
        }
    }

    &__view-side {
        left: 0;

        &--hover {
            &:not(&--active) {
                padding-right: 6px;
                border-radius: 6px 0 0 6px;
            }
        }
    }

    &__view-full {
        right: 0;

        &--hover {
            &:not(&--active) {
                padding-left: 6px;
                border-radius: 0 6px 6px 0;

                &:focus::before {
                    left: 6px;
                }
            }
        }
    }

    &__controls {
        display: flex;
        align-items: center;
        margin: 0 auto;
        width: 388px;
        position: absolute;
        left: 50%;
        transform: translateX(-50%);

        .uikit-tooltip {
            position: absolute;
            left: 50%;
            top: -70%;
        }
    }

    &__global-metrics-toggle-left {
        float: left;
    }

    &__groups-label {
        font-size: 14px;
        line-height: 19px;
        padding-left: 10px;
    }

    &__groups-dropdown-arrow {
        display: inline-block;
        width: 13px;
        height: 7px;
        transition: 0.3s transform;
        color: $banana-bread;
        &--up {
            color: $banana-bread;
            transform: rotate(180deg);
        }
    }

    ul {
        list-style: none;
        margin: 0;
        padding: 0;
        box-shadow: 0 6px 22px 0 rgb(31, 53, 69, 0.1);
        border-radius: 8px;
        position: absolute;
        z-index: 10;
        background: $white;
        left: 138px;
        bottom: 55px;
        li {
            width: 196px;
            height: 43px;
            border-bottom: 1px solid $gray-divider;
            font-size: 14px;
            line-height: 43px;
            letter-spacing: 0.1px;
            padding: 0 20px;
            cursor: pointer;
            box-sizing: border-box;
            color: $brand-black;
            &:last-child {
                border-bottom: 0;
            }
            &.selected {
            font-weight: bold;
                cursor: default;
            }
            &:hover:not(.selected) {
                background: $gray-background;
                border-radius: 8px;
            }
        }
    }

    &__previous-arrow,
    &__next-arrow,
    &__top-previous-arrow,
    &__top-next-arrow {
        &--disabled {
            opacity: 0.35;
        }

        &:not(&--disabled) {
            cursor: pointer;

            @media (hover: hover) {
                &:hover {
                    color: $banana-bread;
                }
            }
        }

        &:focus {
            outline: none;
            color: $banana-bread;
        }
    }

    &__top-previous-arrow,
    &__top-next-arrow {
        width: 24px;
        height: 24px;
    }

    &__previous-arrow,
    &__top-previous-arrow {
        transform: rotate(180deg);
    }

    &__flag {
        width: fit-content;
        background-color: $jet;
        border-radius: 16px;
        height: 31px;
        display: flex;
        justify-content: center;
    }

    &__container {
        width: 273px;
        display: flex;
        justify-content: center;
    }

    &__flag-container {
        display: flex;
        margin: 0 auto;
    }

    &__student-name-text {
        font-size: 14px;
        align-items: center;
        padding: 7px 4px 0 0;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        max-width: 170px;
    }

    &__flagged-text {
        font-size: 14px;
        align-items: center;
        padding: 7px 14px 0 0;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
    }

    &__not-flagged-text {
        font-size: 14px;
        padding: 7px 14px;
        align-items: center;
        color: $cloudy;
    }

    &__flagged-by-student-icon {
        color: $white;
        height: 23px;
        width: 24px;
        padding: 4px 0 0 8px;
    }

    &__icon {
        color: #666f76;
        height: 45px;
        width: 43px;
    }

    &__keyboard-shortcuts-btn {
        position: absolute;
        right: 0;
    }

    :deep(.uikit-question__close-icon) {
        display: none;
    }
}
</style>
